import { useTranslation } from 'react-i18next'
import { AnyToken } from 'src/common/types/models/Tokens'
import Button from 'src/common/ui-kit/Button/Button'
import TokenIcon from 'src/common/ui-kit/TokenIcon/TokenIcon'
import Big from 'big.js'
import clsx from 'clsx'
import { displayAmount } from 'src/common/utils/formatters/number'
import { useAtom } from 'jotai'
import { showOffersToTakeAtom } from '../stores/trade-store'
import { OfferWithTake } from 'src/common/utils/offers/findBestOffers'
import { formatBuyTokenAmountMulti } from './SwapTrade/utils'
import { safeDiv } from 'src/common/utils/big-utils'
import { useMemo } from 'react'

const getMaxDecimals = (amount: Big) => {
  if (amount.lt(1)) {
    return 4
  }
  if (amount.gte(1) && amount.lt(100)) {
    return 2
  }
  return 1
}

type TradeOptionAuthStatus = 'authorized' | 'unauthorized' | 'none'

export interface TradeOptionProps {
  mode: 'swap' | 'limit'
  title: string
  description?: string
  badgeIcon: string
  badgeText: string
  buyToken?: AnyToken
  buyTokenAmount: Big
  sellToken?: AnyToken
  sellTokenAmount: Big
  offers?: OfferWithTake[]
  avgPrice?: number
  active?: boolean
  disabled?: boolean
  onClick?: () => void
}

const TradeOption = ({
  mode,
  title,
  description,
  badgeText,
  offers = [],
  buyToken,
  buyTokenAmount,
  sellToken,
  sellTokenAmount,
  active,
  disabled,
  onClick,
}: TradeOptionProps) => {
  const offersExist = offers.length > 0
  const containerClasses = clsx(
    'p-4 border border-main rounded-md relative',
    active && 'bg-secondary border-main-hover',
    !active && 'hover:bg-secondary border-main-hover cursor-pointer',
    disabled && 'opacity-50 pointer-events-none cursor-not-allowed'
  )
  const { t } = useTranslation('trade')
  const [showOffersToTake, setShowOffersToTake] = useAtom(showOffersToTakeAtom)
  const toggleShowOffersToTake = () => setShowOffersToTake(!showOffersToTake)

  const handleClick = () => {
    if (!active && !disabled) onClick?.()
  }

  const localBuyTokenAmount =
    offers.length > 0 ? formatBuyTokenAmountMulti(offers) : buyTokenAmount

  const avgPrice = safeDiv(sellTokenAmount, localBuyTokenAmount)

  const authStatus = useMemo<TradeOptionAuthStatus>(() => {
    const thereIsAuthorization = offers.some(
      offer => !!offer.authorizationAddresses
    )
    const authorized = offers.every(
      offer =>
        offer.isFullyAuthorized ||
        !offer.authorizationAddresses ||
        offer.authorizationAddresses.length === 0
    )

    if (thereIsAuthorization && authorized) {
      return 'authorized'
    } else if (thereIsAuthorization && !authorized) {
      return 'unauthorized'
    } else {
      return 'none'
    }
  }, [offers])

  if (mode === 'swap' && offers.length === 0) {
    return (
      <div className={containerClasses} onClick={handleClick}>
        <div className='flex justify-between gap-2 mb-2'>
          <div className='flex flex-col gap-2'>
            <span className='typo-label2 text-main-accent font-medium'>
              {title}
            </span>
            {description && <span className='typo-body1'>{description}</span>}
          </div>
          <div className='paper py-0.5 px-2 shadow-md absolute top-2 right-2'>
            <span className='typo-label2'>{badgeText}</span>
          </div>
        </div>
        <div className='flex justify-center items-center h-11'>
          <span className='typo-label2 text-primary'>No offers available</span>
        </div>
      </div>
    )
  }

  return (
    <div className={containerClasses} onClick={handleClick}>
      <div className='flex justify-between gap-2'>
        <div className='flex flex-col gap-2'>
          <span className='typo-label2 text-main-accent font-medium'>
            {title}
          </span>
          {description && <span className='typo-body1'>{description}</span>}
        </div>
        <div className='paper py-0.5 px-2 shadow-md absolute top-2 right-2'>
          <span className='typo-label2'>{badgeText}</span>
        </div>
      </div>
      {mode === 'swap' && (
        <div className='flex justify-between items-center gap-2 mt-5'>
          <div>
            <div className='typo-label1'>From</div>
            <div className='flex items-center gap-1'>
              <TokenIcon token={sellToken} />
              <div className='typo-title3 text-main-accent'>
                {displayAmount(sellTokenAmount, {
                  maxDecimals: getMaxDecimals(sellTokenAmount),
                })}
              </div>
              <div className='typo-label2 text-main-accent'>
                {sellToken?.symbol}
              </div>
            </div>
          </div>
          <div>
            <div className='typo-label1'>To</div>
            <div className='flex items-center gap-1'>
              <TokenIcon token={buyToken} />
              <div className='typo-title3 text-main-accent'>
                {displayAmount(localBuyTokenAmount, {
                  maxDecimals: getMaxDecimals(localBuyTokenAmount),
                })}
              </div>
              <div className='typo-label2 text-main-accent'>
                {buyToken?.symbol}
              </div>
            </div>
          </div>
        </div>
      )}
      {mode === 'swap' && offers.length > 0 && (
        <div className='flex justify-between items-center gap-2 mt-4'>
          <span className='typo-label2'>
            {offers.length > 0
              ? t('tradeOption.offersToTake')
              : t('tradeOption.noOffers')}
          </span>
          {offers.length > 0 && (
            <div className='flex items-center gap-4'>
              <span className='typo-label2'>{offers.length}</span>
              {offersExist && (
                <Button
                  size='sm'
                  variant='text'
                  className='shadow-sm'
                  onClick={toggleShowOffersToTake}
                >
                  {showOffersToTake
                    ? t('tradeOption.hideOffers')
                    : t('tradeOption.viewOffers')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {offersExist && (
        <div className='flex justify-between gap-2 mt-2'>
          <span className='typo-label2'>{t('tradeOption.avgPrice')}</span>
          <div className='flex items-center gap-4'>
            <span className='typo-label2'>
              {displayAmount(avgPrice)} {sellToken?.symbol}/{buyToken?.symbol}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default TradeOption
